/**
 * Development-only overlay of `layoutOverrides.local.js` on the backend layout.
 * See `layoutOverrides.local.example.js` for how to use it.
 */

// `require.context` rather than an import: the local file is ignored by git and
// is normally absent, and importing a missing module would fail the build.
// In production the condition is a constant, so webpack does not walk this
// branch at all and the local file is never bundled.
const localOverridesContext =
  process.env.NODE_ENV !== 'production'
    ? require.context('./', false, /^\.\/layoutOverrides\.local\.js$/)
    : null;

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readLocalOverrides = () => {
  if (!localOverridesContext) {
    return null;
  }

  const [fileKey] = localOverridesContext.keys();
  const overrides = fileKey ? localOverridesContext(fileKey).default : null;

  return isPlainObject(overrides) ? overrides : null;
};

/**
 * Merges the local overrides over the layout sent by the backend, per group,
 * so the precedence is: defaults, then backend, then local values.
 *
 * Only the values the local file actually sets take part: `null` and
 * `undefined` there do not shadow the backend.
 *
 * @param {object} [backendLayout] - The `layout` object of the site configuration
 * @returns {object|undefined} The layout to hand to `resolveLayout`
 */
export const applyLocalLayoutOverrides = (backendLayout) => {
  const overrides = readLocalOverrides();

  if (!overrides) {
    return backendLayout;
  }

  // Keeps an active override from being mistaken for backend data.
  console.info('[nw4] local layout overrides active', overrides);

  const mergedLayout = isPlainObject(backendLayout) ? { ...backendLayout } : {};

  Object.entries(overrides).forEach(([group, values]) => {
    if (!isPlainObject(values)) {
      return;
    }

    const setValues = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== null && value !== undefined)
    );
    const backendGroup = isPlainObject(mergedLayout[group]) ? mergedLayout[group] : {};

    mergedLayout[group] = { ...backendGroup, ...setValues };
  });

  return mergedLayout;
};
